import React, { useEffect, useState } from 'react';
import SPAButton from '../../shared/SPAButton';
import SPACheckbox from '../../shared/SPACheckbox';
import SPAInput from '../../shared/SPAInput';
import SPASelect from '../../shared/SPASelect';
import compactSelectStyles from '../../shared/compactSelectStyles';
import type { SearchField, SearchRole, SearchSubmission } from './types';

interface FieldOption {
  label: string;
  value: SearchField;
}

interface RoleOption {
  label: string;
  value: SearchRole;
}

const FIELD_OPTIONS: FieldOption[] = [
  { label: 'Email', value: 'email' },
  { label: 'Username', value: 'username' },
  { label: 'First name', value: 'first_name' },
  { label: 'Last name', value: 'last_name' },
  { label: 'User ID', value: 'id' },
];

const ROLE_OPTIONS: RoleOption[] = [
  { label: 'All roles', value: 'all' },
  { label: 'Students', value: 'student' },
  { label: 'Teachers', value: 'teacher' },
  { label: 'Coordinators', value: 'coordinator' },
  { label: 'Admins', value: 'admin' },
];

const labelClass = 'tw-mb-1 tw-block tw-text-xs tw-text-spa-secondary';

// The search pane is a scroll container (the rail body, or the sticky legacy pane), so an inline
// menu would grow it and clip. Portal the menu to <body>, positioned against the viewport, the
// way common/ReactSelect5 does; darkSelectStyles already stacks the portal above the page.
const portalMenuProps = {
  menuPlacement: 'auto' as const,
  menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
  menuPosition: 'fixed' as const,
  menuShouldScrollIntoView: false,
};

interface UserSearchBarProps {
  exact: boolean;
  field: SearchField;
  query: string;
  role: SearchRole;
  onSearch: (submission: SearchSubmission) => void;
}

// Edits stay staged until submit; the URL (via props) is the source of truth for what is searched.
const UserSearchBar = ({
  exact, field, query, role, onSearch,
}: UserSearchBarProps) => {
  const [stagedExact, setStagedExact] = useState(exact);
  const [stagedField, setStagedField] = useState(field);
  const [stagedQuery, setStagedQuery] = useState(query);
  const [stagedRole, setStagedRole] = useState(role);

  useEffect(() => { setStagedExact(exact); }, [exact]);
  useEffect(() => { setStagedField(field); }, [field]);
  useEffect(() => { setStagedQuery(query); }, [query]);
  useEffect(() => { setStagedRole(role); }, [role]);

  const fieldOptions = stagedRole === 'student'
    ? FIELD_OPTIONS.filter(option => option.value !== 'email')
    : FIELD_OPTIONS;

  const handleRoleChange = (newRole: SearchRole) => {
    setStagedRole(newRole);
    if (newRole === 'student' && stagedField === 'email') setStagedField('username');
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSearch({
      exact: stagedExact, field: stagedField, q: stagedQuery.trim(), role: stagedRole,
    });
  };

  return (
    <form autoComplete="off" className="tw-mt-4 tw-flex tw-flex-col tw-gap-3" onSubmit={handleSubmit}>
      <div>
        <label className={labelClass} htmlFor="user-search-field">Search by</label>
        <SPASelect<FieldOption>
          {...portalMenuProps}
          inputId="user-search-field"
          options={fieldOptions}
          styles={compactSelectStyles}
          value={fieldOptions.find(option => option.value === stagedField) ?? null}
          onChange={option => option && setStagedField(option.value)}
        />
      </div>
      <div>
        <label className={labelClass} htmlFor="user-search-role">Role</label>
        <SPASelect<RoleOption>
          {...portalMenuProps}
          inputId="user-search-role"
          options={ROLE_OPTIONS}
          styles={compactSelectStyles}
          value={ROLE_OPTIONS.find(option => option.value === stagedRole) ?? null}
          onChange={option => option && handleRoleChange(option.value)}
        />
      </div>
      <div>
        <label className={labelClass} htmlFor="user-search-query">Search</label>
        <SPAInput
          autoComplete="off"
          id="user-search-query"
          value={stagedQuery}
          onChange={event => setStagedQuery(event.target.value)}
        />
      </div>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
        <SPACheckbox
          checked={stagedExact}
          id="user-search-exact"
          onChange={event => setStagedExact(event.target.checked)}
        >
          Exact match
        </SPACheckbox>
        <SPAButton disabled={!stagedQuery.trim()} type="submit" variant="primary">Search</SPAButton>
      </div>
    </form>
  );
};

export default UserSearchBar;
